// Chart of accounts dashboard (shown when accounts exist). Slim orchestrator.
// Every archive/merge/categorize entry point navigates to the real Review pages.

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Account, AccountType, VelaSuggestion } from '../types';
import { COA_TABLE_COLUMNS } from '../constants';
import AccountsTable from './AccountsTable';
import CoaStickyHeader from './CoaStickyHeader';
import CoaModals from './CoaModals';
import { useTableCustomization } from '../../../../hooks/useTableCustomization';
import { visibleOrderedColumns } from '../../../../components/common/tableCustomization';
import { printReport } from '../../../../utils/printReport';
import type { ValidatedRow } from '../../../../hooks/useImportChartOfAccounts';
import { useDashboardFilters } from './useDashboardFilters';
import { useDashboardSelection } from './useDashboardSelection';
import { useDashboardModals } from './useDashboardModals';
import { buildCoaExport } from './buildCoaExport';

interface DashboardProps {
  accounts: Account[];
  suggestions: VelaSuggestion[];
  onCreateAccount: (draft: { number: string; name: string; type: AccountType }) => void;
  onArchiveAccounts: (ids: string[]) => void;
  onMergeAccounts: (keepId: string, dropId: string) => void;
  onDismissSuggestion: (id: string) => void;
  onRowClick: (id: string) => void;
}

export default function Dashboard({
  accounts,
  suggestions,
  onCreateAccount,
  onArchiveAccounts,
  onDismissSuggestion,
  onRowClick,
}: DashboardProps) {
  const navigate = useNavigate();

  const filters = useDashboardFilters(accounts);
  const selection = useDashboardSelection(filters.paged);
  const modals = useDashboardModals();

  const [newAccountPrefill, setNewAccountPrefill] = useState<{ name?: string; type?: AccountType }>({});

  const { customization, draft, updateDraft, applyChanges, cancelDraft, resetToDefault } =
    useTableCustomization('chart-of-accounts', COA_TABLE_COLUMNS);
  const visibleCols = visibleOrderedColumns(customization);

  const openNewAccount = useCallback(
    (prefill?: { name?: string; type?: AccountType }) => {
      setNewAccountPrefill(prefill ?? {});
      modals.setDrawerOpen(true);
    },
    [modals]
  );

  const goToReviewArchive = useCallback(
    () => navigate('/accounting/chart-of-accounts/review-archive'),
    [navigate]
  );
  const goToReviewMerge = useCallback(
    () => navigate('/accounting/chart-of-accounts/review-merge'),
    [navigate]
  );
  const goToReviewCategorize = useCallback(
    () => navigate('/accounting/chart-of-accounts/review-categorize'),
    [navigate]
  );

  const handleSuggestionPrimary = useCallback(
    (s: VelaSuggestion) => {
      // Close the suggestions drawer first so the next drawer or navigation is not hidden behind it.
      modals.setSuggestionsOpen(false);

      if (s.type === 'add') {
        openNewAccount({ name: 'Vehicle expenses', type: 'Expense' });
        onDismissSuggestion(s.id);
        return;
      }
      if (s.type === 'archive') {
        goToReviewArchive();
        return;
      }
      if (s.type === 'merge') {
        goToReviewMerge();
        return;
      }
      if (s.type === 'categorize') {
        goToReviewCategorize();
        return;
      }
      onDismissSuggestion(s.id);
    },
    [modals, openNewAccount, goToReviewArchive, goToReviewMerge, goToReviewCategorize, onDismissSuggestion]
  );

  const handleSuggestionSkip = useCallback(
    (s: VelaSuggestion) => onDismissSuggestion(s.id),
    [onDismissSuggestion]
  );

  const handleBulkMakeInactive = useCallback(() => {
    const ids = Array.from(selection.selectedIds);
    onArchiveAccounts(ids);
    selection.clearSelection();
  }, [selection, onArchiveAccounts]);

  const handleBulkLockAccounts = useCallback(() => {
    const ids = Array.from(selection.selectedIds);
    alert(`Would lock ${ids.length} account${ids.length === 1 ? '' : 's'} (TODO: wire to backend when ready)`);
    selection.clearSelection();
  }, [selection]);

  const handleImportRows = useCallback(
    (rows: ValidatedRow[]) => {
      rows.forEach((row) => {
        onCreateAccount({
          number: row.number || '',
          name: row.name,
          type: row.accountType as AccountType,
        });
      });
    },
    [onCreateAccount]
  );

  const existingAccountNames = useMemo(() => accounts.map((a) => a.name), [accounts]);
  const exportOptions = useMemo(
    () => buildCoaExport(filters.filtered, visibleCols),
    [filters.filtered, visibleCols]
  );

  return (
    <div>
      <CoaStickyHeader
        suggestionsCount={suggestions.length}
        onVelaClick={() => modals.setSuggestionsOpen(true)}
        onNewAccount={() => openNewAccount()}
        onRunReport={() => navigate('/accounting/reports/account-list')}
        onImportChartOfAccounts={() => modals.setImportOpen(true)}
        onCreateMultiple={() => navigate('/accounting/chart-of-accounts/create-multiple')}
        onStartVelaCleanup={() => modals.setVelaCleanupOpen(true)}
        onArchiveUnused={goToReviewArchive}
        onMergeDuplicates={goToReviewMerge}
        onCategorizeUncategorized={goToReviewCategorize}
        onOpenEditMode={() => navigate('/accounting/chart-of-accounts/edit')}
        onViewCleanupHistory={() => navigate('/accounting/chart-of-accounts/cleanup-history')}
        query={filters.query}
        onQueryChange={filters.handleQueryChange}
        typeFilter={filters.typeFilter}
        onTypeFilterChange={filters.handleTypeFilterChange}
        pageStart={filters.pageStart}
        pageEnd={filters.pageEnd}
        total={filters.filtered.length}
        onPrev={filters.prevPage}
        onNext={filters.nextPage}
        onExport={() => modals.setExportOpen(true)}
        onPrint={() => printReport({ documentTitle: 'Chart of accounts' })}
        onCustomize={() => modals.setCustomizeOpen(true)}
        suggestions={suggestions}
        visibleCols={visibleCols}
        sortBy={filters.sortBy}
        sortDir={filters.sortDir}
        onSort={filters.handleSort}
        allPagedSelected={selection.allPagedSelected}
        somePagedSelected={selection.somePagedSelected}
        onToggleAllPaged={selection.toggleAllPaged}
        headerCheckboxRef={selection.headerCheckboxRef}
      />

      <div className="print-area">
        <AccountsTable
          accounts={filters.paged}
          customization={customization}
          selectedIds={selection.selectedIds}
          onToggleRow={selection.toggleRow}
          onRowClick={onRowClick}
          onEdit={(id) =>
            openNewAccount({
              name: accounts.find((a) => a.id === id)?.name,
              type: accounts.find((a) => a.id === id)?.type,
            })
          }
          onRunReport={(id) => navigate(`/accounting/chart-of-accounts/${id}/report`)}
          onReconcile={() => alert('Reconcile (follow-up PR)')}
          onCreateSubaccount={(id) =>
            openNewAccount({
              name: `Subaccount of ${accounts.find((a) => a.id === id)?.name ?? ''}`,
              type: accounts.find((a) => a.id === id)?.type,
            })
          }
          onMakeInactive={(id) => onArchiveAccounts([id])}
        />
      </div>

      <CoaModals
        drawerOpen={modals.drawerOpen}
        closeDrawer={() => modals.setDrawerOpen(false)}
        onCreateAccount={onCreateAccount}
        newAccountPrefill={newAccountPrefill}
        suggestionsOpen={modals.suggestionsOpen}
        closeSuggestions={() => modals.setSuggestionsOpen(false)}
        suggestions={suggestions}
        onSuggestionPrimary={handleSuggestionPrimary}
        onSuggestionSkip={handleSuggestionSkip}
        exportOpen={modals.exportOpen}
        closeExport={() => modals.setExportOpen(false)}
        exportOptions={exportOptions}
        customizeOpen={modals.customizeOpen}
        closeCustomize={() => modals.setCustomizeOpen(false)}
        customizeDraft={draft}
        updateCustomizeDraft={updateDraft}
        applyCustomize={applyChanges}
        cancelCustomize={cancelDraft}
        resetCustomize={resetToDefault}
        selectedCount={selection.selectedIds.size}
        onBulkMakeInactive={handleBulkMakeInactive}
        onBulkLockAccounts={handleBulkLockAccounts}
        onBulkDismiss={selection.clearSelection}
        velaCleanupOpen={modals.velaCleanupOpen}
        closeVelaCleanup={() => modals.setVelaCleanupOpen(false)}
        importOpen={modals.importOpen}
        closeImport={() => modals.setImportOpen(false)}
        existingAccountNames={existingAccountNames}
        onImport={handleImportRows}
      />
    </div>
  );
}